import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbOffcanvas, NgbOffcanvasOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { PlanogramLayout } from 'src/app/models/layout/planogram-layout';
import { ProductReference } from 'src/app/models/layout/product-reference';
import { LayoutService } from 'src/app/services/layout.service';
import { OverlayService, OverlayType } from 'src/app/services/overlay.service';
import { VisualAnalysisService } from 'src/app/services/visualanalysis.service';
import { FormsModule } from '@angular/forms';
import { NgClass, DecimalPipe } from '@angular/common';
import { ColorPickerDirective } from 'ngx-color-picker';
import * as XLSX from 'xlsx';

interface ProductRow {
  product: ProductReference;
  name: string;
  barcode: string;
  /** Size = unit of size + unit of measure, e.g. "500 GRM". */
  size: string;
  /** Sales/volume share within the parent brand. */
  salesPercentage: number;
  /** Space share within the parent brand. */
  spacePercentage: number;
  facings: number;
  facings_recommended: number;
  /** Live days-of-supply (total DOS for this product on the planogram). */
  dos: number;
}

interface BrandRow {
  key: string;
  /** Stable identity across rebuilds: `${segment}::${brand}`. */
  uniqueKey: string;
  products: ProductRow[];
  productRefs: ProductReference[];
  /** Sales/volume share within the parent segment. */
  salesPercentage: number;
  /** Space share within the parent segment. */
  spacePercentage: number;
  facings: number;
  facings_recommended: number;
  /** Sales-qty-weighted average DOS across the brand's products. */
  dos: number;
  count: number;
  color: string;
  visible: boolean;
}

interface SegmentRow {
  key: string;
  brands: BrandRow[];
  productRefs: ProductReference[];
  /** Sales/volume share of the whole planogram. */
  salesPercentage: number;
  /** Space share of the whole planogram. */
  spacePercentage: number;
  facings: number;
  facings_recommended: number;
  /** Sales-qty-weighted average DOS across the segment's products. */
  dos: number;
  count: number;
  visible: boolean;
}

type RootLevel = 'Segment' | 'Family';

@Component({
  selector: 'app-visual-analysis-overlay',
  templateUrl: './visual-analysis-overlay.component.html',
  styleUrl: './visual-analysis-overlay.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [FormsModule, NgClass, ColorPickerDirective, DecimalPipe],
})
export class VisualAnalysisOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('content') content: any;
  options: NgbOffcanvasOptions = {
    backdrop: false,
    position: 'bottom',
    panelClass: 'va-offcanvas-bottom',
  };

  /** Which category level roots the hierarchy. Brand/Product are always inner. */
  rootLevel: RootLevel = 'Segment';
  selectedMetric: 'sales' | 'salesQty' = 'sales';

  hierarchy: SegmentRow[] = [];

  /** Sales-qty-weighted average DOS across the whole planogram (for export). */
  totalDOS = 0;

  // Master-detail selection (drill path through the three panes).
  selectedSegmentKey: string | null = null;
  selectedBrandKey: string | null = null;

  private hiddenSegmentKeys = new Set<string>();
  private hiddenBrandKeys = new Set<string>();

  private subscriptions: Subscription = new Subscription();

  constructor(
    public offcanvas: NgbOffcanvas,
    private visualAnalysisService: VisualAnalysisService,
    private overlayService: OverlayService,
    private layoutService: LayoutService
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.visualAnalysisService.open$.subscribe((isOpen) => {
        if (isOpen) {
          const openVA = () => {
            const ref = this.offcanvas.open(this.content, this.options);
            ref.dismissed.subscribe(() => {
              this.visualAnalysisService.setOpen(false);
            });
            this.overlayService.setOverlayType(OverlayType.VISUAL_ANALYSIS);
            this.updateAnalysis();
          };
          if (this.offcanvas.hasOpenOffcanvas()) {
            // Dismiss the previous offcanvas, then defer opening to the next
            // microtask. ng-bootstrap's NgbOffcanvasStack attaches an
            // unregister callback via result.then() that unconditionally sets
            // _offcanvasRef = undefined; if we open synchronously after
            // dismiss, that microtask runs after our open() and orphans the
            // new ref.
            this.offcanvas.dismiss();
            Promise.resolve().then(openVA);
          } else {
            openVA();
          }
        } else {
          if (this.offcanvas.hasOpenOffcanvas()) {
            this.offcanvas.dismiss();
          }
          this.overlayService.setOverlayType(OverlayType.NONE);
        }
      })
    );
    // Recompute only while the offcanvas is open — the result is never read
    // otherwise and rebuilding traverses the full layout.
    this.subscriptions.add(
      this.layoutService.layout$.subscribe((layout) => {
        if (this.visualAnalysisService.isOpen) {
          this.updateAnalysis(layout);
        }
      })
    );
    this.subscriptions.add(
      this.visualAnalysisService.customColorMap$.subscribe(() => {
        this.overlayService.refreshLegendMap();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get rootLevelLabel(): string {
    return this.rootLevel;
  }

  onHierarchyChange() {
    this.hiddenSegmentKeys.clear();
    this.hiddenBrandKeys.clear();
    this.updateAnalysis();
  }

  private rootKey(product: ProductReference): string {
    return this.rootLevel === 'Segment'
      ? product.categoryLevel2
      : product.categoryLevel3;
  }

  private salesOf(product: ProductReference): number {
    return this.selectedMetric === 'sales'
      ? product.salesData.sales || 0
      : product.salesData.salesQty || 0;
  }

  private pct(part: number, whole: number): number {
    return whole > 0 ? (part / whole) * 100 : 0;
  }

  /** Size column: unit of size + unit of measure, e.g. "500 GRM". */
  private formatSize(product: ProductReference): string {
    return `${product.uos ?? ''} ${product.unit ?? ''}`.trim();
  }

  updateAnalysis(layout: PlanogramLayout | null = null) {
    if (layout == null) layout = this.layoutService.getCurrentLayout();
    if (!layout) return;

    // Facings per product (each placed product instance counts as one facing)
    // and the set of products actually on the planogram.
    const facingsMap = new Map<number, number>();
    layout.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((p) => {
            facingsMap.set(p.productId, (facingsMap.get(p.productId) || 0) + 1);
          });
        });
      });
    });

    const products = Array.from(
      this.layoutService.planogramItems.values()
    ).filter((product) => facingsMap.has(product.productId));

    const linearWidthMap = this.layoutService.getProductsLinearUsedSpace();
    // Live per-product DOS (productId -> total days of supply on the planogram),
    // recomputed and published before layout$ emits, so it is in sync here.
    const dosMap = this.layoutService.getLayoutDOS();

    // Nested grouping: root level -> brand -> products.
    const rootMap = new Map<string, Map<string, ProductReference[]>>();
    let totalSales = 0;
    let totalSpace = 0;
    let totalFacings = 0;
    // Planogram-wide DOS as a sales-qty-weighted average (matches the footer's
    // avgDOSWeighted): Σ(dos·salesQty) / Σ(salesQty), skipping products with
    // no supply or no sales.
    let totalDOSWeightedSum = 0;
    let totalDOSQty = 0;

    products.forEach((product) => {
      totalSales += this.salesOf(product);
      totalSpace += linearWidthMap.get(product.productId) || 0;
      totalFacings += facingsMap.get(product.productId) || 0;

      const prodDOS = dosMap.get(product.productId) || 0;
      const prodSalesQty = product.salesData?.salesQty ?? 0;
      if (prodDOS > 0 && prodSalesQty > 0) {
        totalDOSWeightedSum += prodDOS * prodSalesQty;
        totalDOSQty += prodSalesQty;
      }

      const root = this.rootKey(product);
      const brand = product.brand;
      if (!rootMap.has(root)) {
        rootMap.set(root, new Map<string, ProductReference[]>());
      }
      const brandMap = rootMap.get(root)!;
      if (!brandMap.has(brand)) brandMap.set(brand, []);
      brandMap.get(brand)!.push(product);
    });

    const colorUpdates: [number, string][] = [];

    this.hierarchy = Array.from(rootMap.entries())
      .map(([segmentName, brandMap]) => {
        const segmentRefs: ProductReference[] = [];
        let segmentSales = 0;
        let segmentSpace = 0;
        let segmentFacings = 0;

        brandMap.forEach((brandProducts) => {
          brandProducts.forEach((p) => {
            segmentRefs.push(p);
            segmentSales += this.salesOf(p);
            segmentSpace += linearWidthMap.get(p.productId) || 0;
            segmentFacings += facingsMap.get(p.productId) || 0;
          });
        });

        // Recommended facings cascade top-down: the segment gets its fair share
        // of the whole planogram's facings, then brands split the segment's
        // recommendation, and products split their brand's. This keeps every
        // level's recommendation summing to its parent's — child facings AND
        // recommended facings both add up to the segment row.
        const segmentFacingsRecommended =
          totalFacings * (totalSales > 0 ? segmentSales / totalSales : 0);

        // Segment DOS rolls up the brand weighted sums (Σ dos·salesQty / Σ qty).
        let segmentDOSWeightedSum = 0;
        let segmentDOSQty = 0;

        const brands: BrandRow[] = Array.from(brandMap.entries())
          .map(([brandName, brandProducts]) => {
            const uniqueKey = `${segmentName}::${brandName}`;
            // Colour by brand (not segment::brand) so the same brand reads as
            // one consistent colour across every segment and appears once in
            // the legend.
            const color = this.stringToColor(brandName);
            const visible = !this.hiddenBrandKeys.has(uniqueKey);

            let brandSales = 0;
            let brandSpace = 0;
            let brandFacings = 0;
            brandProducts.forEach((p) => {
              brandSales += this.salesOf(p);
              brandSpace += linearWidthMap.get(p.productId) || 0;
              brandFacings += facingsMap.get(p.productId) || 0;
            });

            if (visible) {
              brandProducts.forEach((p) =>
                colorUpdates.push([p.productId, color])
              );
            }

            const brandFacingsRecommended =
              segmentFacingsRecommended *
              (segmentSales > 0 ? brandSales / segmentSales : 0);

            let brandDOSWeightedSum = 0;
            let brandDOSQty = 0;

            const productRows: ProductRow[] = brandProducts
              .map((p) => {
                const prodSales = this.salesOf(p);
                const prodSpace = linearWidthMap.get(p.productId) || 0;
                const prodFacings = facingsMap.get(p.productId) || 0;
                const prodDOS = dosMap.get(p.productId) || 0;
                const prodSalesQty = p.salesData?.salesQty ?? 0;
                if (prodDOS > 0 && prodSalesQty > 0) {
                  brandDOSWeightedSum += prodDOS * prodSalesQty;
                  brandDOSQty += prodSalesQty;
                }
                return {
                  product: p,
                  name: p.description,
                  barcode: p.barcode,
                  size: this.formatSize(p),
                  // Share within the parent brand.
                  salesPercentage: this.pct(prodSales, brandSales),
                  spacePercentage: this.pct(prodSpace, brandSpace),
                  facings: prodFacings,
                  // Split the brand's recommendation by sales share.
                  facings_recommended:
                    brandFacingsRecommended *
                    (brandSales > 0 ? prodSales / brandSales : 0),
                  dos: prodDOS,
                };
              })
              .sort((a, b) => b.salesPercentage - a.salesPercentage);

            segmentDOSWeightedSum += brandDOSWeightedSum;
            segmentDOSQty += brandDOSQty;

            return {
              key: brandName,
              uniqueKey,
              products: productRows,
              productRefs: brandProducts,
              // Share within the parent segment.
              salesPercentage: this.pct(brandSales, segmentSales),
              spacePercentage: this.pct(brandSpace, segmentSpace),
              facings: brandFacings,
              // Split the segment's recommendation by sales share.
              facings_recommended: brandFacingsRecommended,
              dos: brandDOSQty > 0 ? brandDOSWeightedSum / brandDOSQty : 0,
              count: brandProducts.length,
              color,
              visible,
            };
          })
          .sort((a, b) => b.salesPercentage - a.salesPercentage);

        return {
          key: segmentName,
          brands,
          productRefs: segmentRefs,
          // Share of the whole planogram.
          salesPercentage: this.pct(segmentSales, totalSales),
          spacePercentage: this.pct(segmentSpace, totalSpace),
          facings: segmentFacings,
          facings_recommended: segmentFacingsRecommended,
          dos: segmentDOSQty > 0 ? segmentDOSWeightedSum / segmentDOSQty : 0,
          count: segmentRefs.length,
          visible: !this.hiddenSegmentKeys.has(segmentName),
        };
      })
      .sort((a, b) => b.salesPercentage - a.salesPercentage);

    this.totalDOS = totalDOSQty > 0 ? totalDOSWeightedSum / totalDOSQty : 0;

    this.ensureSelection();
    this.visualAnalysisService.setProductColors(colorUpdates);
  }

  // --- Master-detail selection --------------------------------------------

  /** Keep the drill path valid after a rebuild, defaulting to the first row. */
  private ensureSelection() {
    if (this.hierarchy.length === 0) {
      this.selectedSegmentKey = null;
      this.selectedBrandKey = null;
      return;
    }
    let segment = this.hierarchy.find((s) => s.key === this.selectedSegmentKey);
    if (!segment) {
      segment = this.hierarchy[0];
      this.selectedSegmentKey = segment.key;
      this.selectedBrandKey = null;
    }
    if (!segment.brands.some((b) => b.uniqueKey === this.selectedBrandKey)) {
      this.selectedBrandKey = segment.brands[0]?.uniqueKey ?? null;
    }
  }

  get selectedSegment(): SegmentRow | undefined {
    return this.hierarchy.find((s) => s.key === this.selectedSegmentKey);
  }

  get selectedBrand(): BrandRow | undefined {
    return this.selectedSegment?.brands.find(
      (b) => b.uniqueKey === this.selectedBrandKey
    );
  }

  selectSegment(segment: SegmentRow) {
    this.selectedSegmentKey = segment.key;
    this.selectedBrandKey = segment.brands[0]?.uniqueKey ?? null;
  }

  selectBrand(brand: BrandRow) {
    this.selectedBrandKey = brand.uniqueKey;
  }

  stringToColor(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash | 0);
    const color = hash % 0xffffff;
    let hexColor = color.toString(16);
    hexColor = '#' + ('000000' + hexColor).slice(-6);
    return hexColor;
  }

  // --- Visibility ----------------------------------------------------------

  /** A brand is effectively shown only when both it and its segment are on. */
  private isBrandEffectivelyVisible(
    segment: SegmentRow,
    brand: BrandRow
  ): boolean {
    return segment.visible && brand.visible;
  }

  areAllGroupsVisible(): boolean {
    return this.hierarchy.every(
      (segment) =>
        segment.visible && segment.brands.every((brand) => brand.visible)
    );
  }

  toggleAllGroupsVisibility() {
    const showAll = !this.areAllGroupsVisible();
    this.hierarchy.forEach((segment) => {
      segment.visible = showAll;
      if (showAll) this.hiddenSegmentKeys.delete(segment.key);
      else this.hiddenSegmentKeys.add(segment.key);

      segment.brands.forEach((brand) => {
        brand.visible = showAll;
        if (showAll) this.hiddenBrandKeys.delete(brand.uniqueKey);
        else this.hiddenBrandKeys.add(brand.uniqueKey);
        this.applyBrandColors(brand, showAll);
      });
    });
  }

  toggleSegmentVisibility(segment: SegmentRow) {
    segment.visible = !segment.visible;
    if (segment.visible) this.hiddenSegmentKeys.delete(segment.key);
    else this.hiddenSegmentKeys.add(segment.key);

    // Re-apply colors for each brand based on the new effective visibility.
    segment.brands.forEach((brand) => {
      this.applyBrandColors(
        brand,
        this.isBrandEffectivelyVisible(segment, brand)
      );
    });
  }

  toggleBrandVisibility(segment: SegmentRow, brand: BrandRow) {
    brand.visible = !brand.visible;
    if (brand.visible) this.hiddenBrandKeys.delete(brand.uniqueKey);
    else this.hiddenBrandKeys.add(brand.uniqueKey);
    this.applyBrandColors(
      brand,
      this.isBrandEffectivelyVisible(segment, brand)
    );
  }

  private applyBrandColors(brand: BrandRow, show: boolean) {
    if (show) {
      this.visualAnalysisService.setProductColors(
        brand.productRefs.map((p) => [p.productId, brand.color])
      );
    } else {
      this.visualAnalysisService.clearProductColors(
        brand.productRefs.map((p) => p.productId)
      );
    }
  }

  onBrandColorChange(segment: SegmentRow, brand: BrandRow) {
    if (this.isBrandEffectivelyVisible(segment, brand)) {
      this.applyBrandColors(brand, true);
    }
  }

  /** Export the nested Segment → Brand → Product analysis to an .xlsx file,
   *  one row per node with the level filled into the matching column. */
  exportToExcel() {
    if (!this.hierarchy.length) return;

    const round1 = (n: number) => Math.round(n * 10) / 10;
    const metricLabel = this.selectedMetric === 'sales' ? 'Sales %' : 'Volume %';

    const rows: (string | number)[][] = [
      [this.rootLevel, 'Brand', 'Product', 'Barcode', 'Size', metricLabel, 'Space %', 'DOS', 'Facings', 'Facings R', '# Products'],
    ];

    let totalFacings = 0;
    let totalProducts = 0;
    this.hierarchy.forEach((s) => {
      totalFacings += s.facings;
      totalProducts += s.count;
    });
    rows.push(['Total planogram', '', '', '', '', 100, 100, round1(this.totalDOS), totalFacings, totalFacings, totalProducts]);

    this.hierarchy.forEach((segment) => {
      rows.push([
        segment.key, '', '', '', '',
        round1(segment.salesPercentage), round1(segment.spacePercentage), round1(segment.dos),
        segment.facings, Math.round(segment.facings_recommended), segment.count,
      ]);
      segment.brands.forEach((brand) => {
        rows.push([
          '', brand.key, '', '', '',
          round1(brand.salesPercentage), round1(brand.spacePercentage), round1(brand.dos),
          brand.facings, Math.round(brand.facings_recommended), brand.count,
        ]);
        brand.products.forEach((p) => {
          rows.push([
            '', '', p.name, p.barcode, p.size,
            round1(p.salesPercentage), round1(p.spacePercentage), round1(p.dos),
            p.facings, Math.round(p.facings_recommended), '',
          ]);
        });
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 24 }, { wch: 38 }, { wch: 16 }, { wch: 12 }, { wch: 10 },
      { wch: 10 }, { wch: 8 }, { wch: 9 }, { wch: 10 }, { wch: 11 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visual Analysis');

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Visual Analysis - ${this.rootLevel} - ${stamp}.xlsx`);
  }

  clearAnalysis() {
    this.visualAnalysisService.clearAllColors();
    this.hierarchy = [];
    this.hiddenSegmentKeys.clear();
    this.hiddenBrandKeys.clear();
  }

  close() {
    this.visualAnalysisService.toggle();
  }
}
